/**
 * O endereço carrega de quem é a tela.
 *
 * Até aqui o painel morava em `/home`, `/bio`, `/settings/branding`, iguais para
 * todo cliente. Quem atende dois abria duas abas idênticas e não sabia qual era
 * qual; um print de suporte não dizia de qual conta era; e o endereço não
 * combinava com o que a gente vende, que é uma área da empresa dele.
 *
 * Agora tudo do cliente vive sob o slug: `/russirussi/home`,
 * `/russirussi/settings/branding`. A Yard continua sem prefixo, porque o console
 * dela não é de cliente nenhum: `/yard`, `/admin/clientes`, `/login`.
 *
 * O prefixo não é reescrito link a link. Seriam quase cem lugares, cada um uma
 * chance de esquecer. Em vez disso, `Link`, `NavLink` e `useNavigate` daqui
 * fazem isso sozinhos: quem escreve `to="/bio"` continua escrevendo `to="/bio"`,
 * e o destino sai `/russirussi/bio`. Trocar o import é o que cada tela precisa
 * fazer, e é uma linha por arquivo.
 *
 * A fonte do slug é a organização ativa da sessão, nunca a URL. A URL é o que o
 * visitante digita, e digitar o slug de outro cliente não pode virar acesso a
 * ele: quem manda é a sessão, e o `RouteGuard` corrige o endereço quando os
 * dois discordam.
 */

import { forwardRef, useCallback, useEffect, useRef } from 'react';
import {
  Link as LinkBase,
  NavLink as NavLinkBase,
  useNavigate as useNavigateBase,
  type LinkProps,
  type NavLinkProps,
  type NavigateOptions,
  type To,
} from 'react-router-dom';

import { useAuth } from './auth';
import { clienteDoHost, ehLocalhost, ehSubdominioDeMail } from './dominios';
import { saltarParaSubdominio } from '../pages/auth/HandoffSubdominio';

/**
 * Caminhos que não pertencem a cliente nenhum e por isso nunca ganham prefixo.
 *
 * A porta da equipe, as telas de conta que existem antes de haver organização
 * (entrar, criar senha, aceitar convite) e as superfícies públicas do Bio, que
 * são endereço do visitante e não do painel.
 */
const SEM_PREFIXO = [
  '/_sso',
  '/_ponte-meet',
  '/login',
  '/yard',
  '/admin',
  '/entrar',
  '/criar-conta',
  '/criar-organizacao',
  '/esqueci-senha',
  '/redefinir-senha',
  '/aceitar-convite',
  '/403',
  '/500',
  '/suspended',
  '/p/',
  '/f/',
  '/d/',
  '/dp/',
  '/b/',
  '/i/',
  '/s/',
  '/l/',
  // Portal: as três superfícies sem sessão. O visitante não tem organização,
  // então prefixá-las com o slug daria um endereço que ninguém consegue montar.
  '/agendar/',
  '/doc/',
  '/sala/',
  // A sala de reunião é rota própria, fora do painel e do suitemail
  // (`app/Router.tsx`, `/reuniao/:sala`): quem chega por link não tem
  // organização, e prefixar daria `/cima/reuniao/x`, que não existe.
  '/reuniao/',
];

const ehGlobal = (caminho: string) =>
  SEM_PREFIXO.some((p) => caminho === p || caminho === p.replace(/\/$/, '') || caminho.startsWith(p));

/** Prefixa um caminho absoluto do painel com o slug do cliente. */
export function comSlug(destino: To, slug: string | null | undefined): To {
  if (!slug) return destino;

  if (typeof destino === 'string') {
    if (!destino.startsWith('/') || ehGlobal(destino)) return destino;
    // Já prefixado: acontece quando alguém monta o caminho à mão a partir do
    // `location` atual. Prefixar de novo daria `/russirussi/russirussi/bio`.
    if (destino === `/${slug}` || destino.startsWith(`/${slug}/`)) return destino;
    return `/${slug}${destino}`;
  }

  const p = destino.pathname;
  if (!p || !p.startsWith('/') || ehGlobal(p)) return destino;
  if (p === `/${slug}` || p.startsWith(`/${slug}/`)) return destino;
  return { ...destino, pathname: `/${slug}${p}` };
}

/**
 * O caminho de um destino do suitemail, visto do host em que estamos.
 *
 * O suitemail tem dois endereços: `/{slug}/mail/...` (localhost e o caminho
 * antigo) e `{slug}.mail.yardsuite.com.br/...` (a porta própria, onde o
 * `<Suitemail />` é montado na RAIZ - `app/Router.tsx`, `ehMail`, rota
 * `/*`). Todo link do produto é escrito na primeira forma (`/mail/agenda`,
 * o rail em `Casca.tsx`, "Ver ata" em `Reunioes.tsx`, o atalho da Caixa para
 * a Config). Na porta própria esse caminho não existe: `/mail/agenda` caía
 * no `*` das rotas aninhadas, que manda para `/mail`, que caía no `*` de
 * novo - o rail inteiro levava para a caixa de entrada, e o "voltar" do
 * navegador ficava preso num laço de redirecionamento. Aqui `/mail/x` vira
 * `/x` quando o host é o de mail; nos outros hosts nada muda.
 */
export function noHostDeMail(destino: To, host: string): To {
  if (!ehSubdominioDeMail(host)) return destino;
  const tirar = (p: string) => (p === '/mail' ? '/' : p.startsWith('/mail/') ? p.slice('/mail'.length) : p);
  if (typeof destino === 'string') return tirar(destino);
  const p = destino.pathname;
  return p ? { ...destino, pathname: tirar(p) } : destino;
}

/**
 * O slug do cliente cuja área está aberta, para PREFIXAR caminho - nulo para
 * a equipe Yard, e também nulo em `{slug}.yardsuite.com.br`: ali o
 * subdomínio já É o slug, prefixar de novo daria `/cima/cima/home`. Só em
 * `yardsuite.com.br` (raiz) e em desenvolvimento o caminho ainda carrega o
 * slug (`core/dominios.ts`, `clienteDoHost`).
 *
 * Nulo também em `{slug}.mail.yardsuite.com.br` e em `mail.yardsuite.com.br`
 * puro: ali `clienteDoHost` devolve nulo (o subdomínio tem ponto, ou é o
 * reservado `mail`), e o `org.slug` da sessão entrava na frente de todo link
 * do suitemail - `/cima/mail/agenda` num host onde o produto mora na raiz.
 */
export function useSlugDoCliente(): string | null {
  const { org } = useAuth();
  if (typeof window !== 'undefined') {
    const host = window.location.host;
    if (clienteDoHost(host) || ehSubdominioDeMail(host)) return null;
  }
  return org?.slug ?? null;
}

/** `comSlug` e `noHostDeMail` juntos: o destino como este host o entende. */
export function destinoNoHost(destino: To, slug: string | null | undefined): To {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  return noHostDeMail(comSlug(destino, slug), host);
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, ...resto }, ref) {
  const slug = useSlugDoCliente();
  return <LinkBase ref={ref} to={destinoNoHost(to, slug)} {...resto} />;
});

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink({ to, ...resto }, ref) {
  const slug = useSlugDoCliente();
  return <NavLinkBase ref={ref} to={destinoNoHost(to, slug)} {...resto} />;
});

export function useNavigate() {
  const navegar = useNavigateBase();
  const slug = useSlugDoCliente();
  // A2: o slug muda quando a organização ativa muda (trocar de conta, entrar
  // numa visita). Fechar sobre `slug` direto no corpo prendia o handler no
  // valor do render em que foi criado - um clique que dispara um `await` e só
  // navega depois usava o slug de ANTES da troca, porque a closure nunca via
  // o valor novo. Um `ref` atualizado a cada render lê o slug no momento da
  // chamada, não no momento em que o handler foi montado.
  const slugRef = useRef(slug);
  useEffect(() => {
    slugRef.current = slug;
  }, [slug]);
  return useCallback(
    (destino: To | number, opcoes?: NavigateOptions) => {
      // `navigate(-1)` é histórico, não endereço: passa direto.
      if (typeof destino === 'number') return navegar(destino);
      return navegar(destinoNoHost(destino, slugRef.current), opcoes);
    },
    [navegar],
  );
}

/**
 * A1/A3/A5/A6: entrar na conta de um cliente - do console, pelo seletor de
 * organização no menu, ou voltando de uma visita - é sempre a mesma decisão:
 * trocar de HOST (produção, vhost curinga por slug) ou só de caminho
 * (localhost, sem vhost curinga). Antes cada um dos três lugares fazia um
 * `navigate` isolado que, fora do dev, sempre caía em 404: o bloco de rotas
 * do cliente só monta em `{slug}.yardsuite.com.br` e em `localhost`, nunca em
 * `yardsuite.com.br` puro - foi decisão explícita matar
 * `yardsuite.com.br/{cliente}/...` fora do dev (`app/Router.tsx`).
 *
 * Recebe o slug de quem chama (quem acabou de trocar de conta já sabe qual
 * é, não precisa esperar o próximo render pegar o valor novo do contexto -
 * é a mesma armadilha do `useNavigate` acima, só que sem `ref` que resolva:
 * aqui o valor não existe ainda no contexto no instante da chamada).
 */
export function useIrParaAConta() {
  const navegar = useNavigateBase();
  return useCallback(
    async (slug: string, destino: string) => {
      const host = typeof window !== 'undefined' ? window.location.host : '';
      if (ehLocalhost(host)) {
        // Sem vhost curinga: o caminho por slug continua sendo o único jeito
        // de testar a área do cliente.
        navegar(`/${slug}${destino}`);
        return;
      }
      await saltarParaSubdominio(slug, destino);
    },
    [navegar],
  );
}
